import { useEffect, useRef } from 'preact/hooks';

import { CountryCodeSelectorProvider } from '@/context/CountryCodeSelectorContext';
import { NavigationContext } from '@/context/NavigationContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import {
  WIDGET_BODY_BOTTOM_OFFSET,
  WIDGET_BODY_BOTTOM_OFFSET_MOBILE,
} from '@/helpers/constants/layout';
import { ROUTE_COMPONENTS } from '@/helpers/constants/routes';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNavigationBase } from '@/hooks/useNavigationBase';
import { E_FloatingWidgetTriggerLocation } from '@/types/FloatingWidgetTrigger';
import type { I_WidgetBodyProps } from '@/types/WidgetBody';
import { cn } from '@/ui/utils';
import { Login } from '@/views/Login';

import { WidgetFooter } from './WidgetFooter';
import { WidgetHeader } from './WidgetHeader';

export const WidgetBody = ({ isWidgetOpen, closeWidget }: I_WidgetBodyProps) => {
  const widgetRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const { widgetLocation, isRegistered, phoneNumber } = useWidgetConfig();

  const navigation = useNavigationBase({ isRegistered, phoneNumber });
  const { activeRoute } = navigation;

  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isWidgetOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWidget();
    };

    closeButtonRef.current?.focus();
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isWidgetOpen]);

  if (!isWidgetOpen) return null;

  const positionClass = cn(
    'fixed',
    isMobile ? WIDGET_BODY_BOTTOM_OFFSET_MOBILE : WIDGET_BODY_BOTTOM_OFFSET,
    widgetLocation === E_FloatingWidgetTriggerLocation.BOTTOM_LEFT ? 'left-6' : 'right-6',
  );

  const renderCurrentView = () => {
    return ROUTE_COMPONENTS[activeRoute] || <Login />;
  };

  return (
    <NavigationContext.Provider value={navigation}>
      <div
        ref={widgetRef}
        id='floating-widget-body'
        className={cn(
          positionClass,
          isMobile ? 'w-[85vw]' : 'w-96',
          'h-[80vh]',
          'rounded-lg border bg-white shadow-xl',
          'animate-slide-up z-50 flex flex-col border border-gray-200',
        )}
      >
        <WidgetHeader closeWidget={closeWidget} closeButtonRef={closeButtonRef} />
        <CountryCodeSelectorProvider>
          <div className='flex-1 overflow-y-auto px-6 pb-6'>{renderCurrentView()}</div>
        </CountryCodeSelectorProvider>
        <WidgetFooter />
      </div>
    </NavigationContext.Provider>
  );
};
